import { FC } from "react";
import { Tooltip } from "antd";

import useElectronicLearningChart, { Props, ReceivedProps } from "../hook";
import {
  StyledFlowV2Wrapper,
  StyledCalloutBubble,
  StyledHoverTooltip,
} from "./styled";
import ZoomContainer from "@lhd/components/ZoomContainer";
import FlowDiamond from "../components/FlowDiamond";
import { ExternalLinkIcon } from "@lhd/assets";

const ElectronicLearningChartV2Layout: FC<Props> = ({
  isShowNote,
  setIsShowNote,
  scale,
  hasAddCoursePermission,
  hasELearningListPermission,
  isAdmin,
}) => {
  return (
    <ZoomContainer enablePanning minScale={0.5}>
      <StyledFlowV2Wrapper scale={scale}>
        <div className="scalable-wrapper">
          {/* ─── 2. Main Flow Container ─── */}
          <div className="v2-flow-container" style={{ position: "relative" }}>
            {/* ══════════════════════════════════════════════════════════════
                TOPMOST LAYER (TOP BANNER ROW):
                - Button "解説表示" (on the left sidebar)
                - 2 "社外と共有" banners for the 2 columns (at the top layer)
                ══════════════════════════════════════════════════════════════ */}
            <div className="v2-top-banner-row">
              <div className="top-sidebar-btn">
                <button
                  className={`btn-show-note inactive`}
                  onClick={() => setIsShowNote(!isShowNote)}
                >
                  {isShowNote ? "解説非表示" : "解説表示"}
                </button>
              </div>
              <div className="top-banners-wrapper">
                <div className="banner-left">社内で活用</div>
                <div className="banner-right">社外と共有</div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                PHASE 1: Bizシート 作成・利用
                - Sidebar: Bizシート 作成・利用 block
                - Body: 2 flow chart columns with gray background #edf2f7
                ══════════════════════════════════════════════════════════════ */}
            <div className="v2-main-phase-row">
              <div className="v2-sidebar-block phase-main">
                Bizシート
                <br />
                作成・利用
              </div>
              <div className="v2-main-content-canvas">
                {/* Gray background panels for the 2 columns */}
                <div className="col-panel-body-bg left-panel" />
                <div className="col-panel-body-bg right-panel" />

                {/* Elements Layer */}
                <div className="elements-layer-full">
                  {/* ──────────────────────────────────────────────────────────
                      LAYER 1: Eラーニング (DEDICATED TOP LAYER, TOP: 16PX)
                      ────────────────────────────────────────────────────────── */}
                  <div
                    className="btn-capsule"
                    style={{
                      position: "absolute",
                      left: 24,
                      top: 16,
                      width: 168,
                      height: 28,
                      cursor: "default",
                      pointerEvents: "none",
                    }}
                  >
                    Eラーニング
                  </div>

                  {/* ──────────────────────────────────────────────────────────
                      LAYER 2: ROW OF 3 PEER BOXES, ALIGNED AT TOP: 76PX
                      ────────────────────────────────────────────────────────── */}
                  {/* Box 1 (Left Column): コースを新規追加 (Shifted left: LEFT 245PX) */}
                  <div
                    className="node-box border-navy"
                    style={{
                      position: "absolute",
                      left: 245,
                      top: 76,
                      width: 150,
                      height: 28,
                    }}
                  >
                    コースを新規追加
                  </div>

                  {/* Box 2 (Left Column): E-ラーニングを視聴・編集 (Shifted right: LEFT 615PX) */}
                  <div
                    className="node-box border-navy"
                    style={{
                      position: "absolute",
                      left: 615,
                      top: 76,
                      width: 172,
                      height: 28,
                    }}
                  >
                    E-ラーニングを視聴・編集
                  </div>

                  {/* Box 3 (Right Column): 取引先とBizTreasureで動画共有 (Shifted right: LEFT 918PX) */}
                  <div
                    className="node-box border-navy"
                    style={{
                      position: "absolute",
                      left: 935,
                      top: 76,
                      width: 250,
                      height: 28,
                    }}
                  >
                    取引先とBizTreasureで動画共有
                  </div>

                  {/* ──────────────────────────────────────────────────────────
                      LAYER 3: DIAMONDS & BRANCH LABELS (TOP: 136PX)
                      ────────────────────────────────────────────────────────── */}
                  {/* Diamond (Left Column): コースの追加方法 (Shifted left: LEFT 235PX) */}
                  <div
                    style={{
                      position: "absolute",
                      left: 235,
                      top: 136,
                    }}
                  >
                    <FlowDiamond
                      name="コースの追加方法"
                      width={170}
                      height={40}
                      fontSize={12}
                    />
                  </div>

                  {/* Branch Label Left: 自社で追加 (Shifted left) */}
                  <div
                    className="branch-label"
                    style={{
                      position: "absolute",
                      left: 140,
                      top: 136,
                    }}
                  >
                    自社で追加
                  </div>

                  {/* Branch Label Right: ストアからダウンロード (Extended spacing) */}
                  <div
                    className="branch-label"
                    style={{
                      position: "absolute",
                      left: 400,
                      top: 136,
                    }}
                  >
                    ストアからダウンロード
                  </div>

                  {/* Diamond (Right Column): アカウントの有無 (Shifted right) */}
                  <div
                    style={{
                      position: "absolute",
                      left: 975,
                      top: 136,
                    }}
                  >
                    <FlowDiamond
                      name="アカウントの有無"
                      width={170}
                      height={40}
                      fontSize={12}
                    />
                  </div>

                  {/* Branch Label Left: あり (Shifted right) */}
                  <div
                    className="branch-label"
                    style={{
                      position: "absolute",
                      left: 935,
                      top: 136,
                    }}
                  >
                    あり
                  </div>

                  {/* Branch Label Right: なし (Shifted right) */}
                  <div
                    className="branch-label"
                    style={{
                      position: "absolute",
                      left: 1151,
                      top: 136,
                    }}
                  >
                    なし
                  </div>

                  {/* ──────────────────────────────────────────────────────────
                      LAYER 4: ROW OF CARDS & MIDDLE BOX (TOP: 200PX)
                      ────────────────────────────────────────────────────────── */}
                  {/* Card (Left Column): コース・レッスンの追加 (Shifted left: LEFT 20PX) */}
                  <div
                    className="flow-card"
                    style={{
                      position: "absolute",
                      left: 20,
                      top: 200,
                      width: 170,
                      height: 68,
                    }}
                  >
                    <div className="card-label">コース・レッスンの追加</div>
                    {hasAddCoursePermission ? (
                      <button
                        className="card-action-btn"
                        onClick={() =>
                          window.open(
                            "/e-learning/courses/create?isDisplayInNewTab=true&fromChart=true",
                            "_blank",
                          )
                        }
                      >
                        ＋コース新規 <ExternalLinkIcon />
                      </button>
                    ) : (
                      <StyledHoverTooltip bg="#2d3748" borderRadius={0}>
                        <div className="custom-tooltip-content">
                          権限がありません
                        </div>
                        <div style={{ width: "100%" }}>
                          <button className="card-action-btn disabled" disabled>
                            ＋コース新規 <ExternalLinkIcon />
                          </button>
                        </div>
                      </StyledHoverTooltip>
                    )}
                    {isShowNote && (
                      <StyledCalloutBubble
                        placement="bottom"
                        style={{ top: 76, left: 16 }}
                      >
                        <p>
                          コースの概要を
                          <br />
                          入力･設定します
                        </p>
                      </StyledCalloutBubble>
                    )}
                  </div>

                  {/* Intermediate Box: 他社に動画を共有・販売 (Shifted left: LEFT 220PX) */}
                  <div
                    className="node-box"
                    style={{
                      position: "absolute",
                      left: 235,
                      top: 217,
                      width: 172,
                      height: 28,
                      cursor: "default",
                    }}
                  >
                    他社に動画を共有・販売
                    {isShowNote && (
                      <StyledCalloutBubble
                        placement="bottom"
                        style={{ bottom: -70, left: 12 }}
                      >
                        <p style={{ fontWeight: 600, textAlign: "left" }}>
                          他社へ公開･販売も可
                          <br />
                          ① 無料（承認制）
                          <br />② 有料
                        </p>
                      </StyledCalloutBubble>
                    )}
                  </div>

                  {/* Card (Left Column): コース・レッスンをダウンロード (Shifted left: LEFT 422PX) */}
                  <div
                    className="flow-card"
                    style={{
                      position: "absolute",
                      left: 437,
                      top: 200,
                      width: 200,
                      height: 68,
                    }}
                  >
                    <div className="card-label">
                      コース・レッスンをダウンロード
                    </div>
                    {hasELearningListPermission ? (
                      <button
                        className="card-action-btn"
                        onClick={() =>
                          window.open(
                            "e-learning/store?isDisplayInNewTab=true",
                            "_blank",
                          )
                        }
                      >
                        E-ラーニングストア <ExternalLinkIcon />
                      </button>
                    ) : (
                      <StyledHoverTooltip bg="#2d3748" borderRadius={0}>
                        <div className="custom-tooltip-content">
                          権限がありません
                        </div>
                        <div style={{ width: "100%" }}>
                          <button className="card-action-btn disabled" disabled>
                            E-ラーニングストア <ExternalLinkIcon />
                          </button>
                        </div>
                      </StyledHoverTooltip>
                    )}
                  </div>

                  {/* Card (Right Column): 契約のご提案 (Shifted right) */}
                  <div
                    className="flow-card"
                    style={{
                      position: "absolute",
                      left: 1126,
                      top: 200,
                      width: 150,
                      height: 68,
                    }}
                  >
                    <div className="card-label">契約のご提案</div>
                    <button
                      className="card-action-btn"
                      onClick={() =>
                        window.open(
                          "/contract_management?isDisplayInNewTab=true",
                          "_blank",
                        )
                      }
                    >
                      契約管理 <ExternalLinkIcon />
                    </button>
                  </div>

                  {/* Card (Right Column): 情報共有の開通申請 (Shifted right) */}
                  <div
                    className="flow-card"
                    style={{
                      position: "absolute",
                      left: 836,
                      top: 200,
                      width: 175,
                      height: 68,
                    }}
                  >
                    <div className="card-label">情報共有の開通申請</div>
                    <StyledHoverTooltip bg="#6BC77A" borderRadius={0}>
                      <div className="custom-tooltip-content">開発中</div>
                      <div style={{ width: "100%" }}>
                        <button className="card-action-btn disabled" disabled>
                          共有企業リスト <ExternalLinkIcon />
                        </button>
                      </div>
                    </StyledHoverTooltip>
                  </div>

                  {/* ──────────────────────────────────────────────────────────
                      LAYER 5: BOTTOM ROW (TOP: 315PX)
                      - レコード情報の閲覧・編集 aligned with E-ラーニングを視聴・編集 (LEFT: 615PX)
                      - 申請を承認 (Right Column)
                      ────────────────────────────────────────────────────────── */}
                  {/* Card (Left Column): レコード情報の閲覧・編集 */}
                  <div
                    className="flow-card"
                    style={{
                      position: "absolute",
                      left: 615,
                      top: 315,
                      width: 172,
                      height: 68,
                    }}
                  >
                    <div className="card-label">レコード情報の閲覧・編集</div>
                    {hasELearningListPermission ? (
                      <button
                        className="card-action-btn"
                        onClick={() =>
                          window.open(
                            "e-learning?isDisplayInNewTab=true",
                            "_blank",
                          )
                        }
                      >
                        E-ラーニング一覧 <ExternalLinkIcon />
                      </button>
                    ) : (
                      <StyledHoverTooltip bg="#2d3748" borderRadius={0}>
                        <div className="custom-tooltip-content">
                          権限がありません
                        </div>
                        <div style={{ width: "100%" }}>
                          <button className="card-action-btn disabled" disabled>
                            E-ラーニング一覧 <ExternalLinkIcon />
                          </button>
                        </div>
                      </StyledHoverTooltip>
                    )}
                  </div>

                  {/* Node Box (Right Column): 申請を承認 */}
                  <div
                    className="node-box"
                    style={{
                      position: "absolute",
                      left: 836,
                      top: 315,
                      width: 175,
                      height: 28,
                    }}
                  >
                    申請を承認
                  </div>
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                PHASE 2: 権限設定
                - Sidebar: 権限設定 block (height: 96px)
                - Body: Gray background canvas #edf2f7
                - Card: コースごとに権限設定 (Aligned with レコード情報の閲覧・編集 at LEFT: 615PX)
                ══════════════════════════════════════════════════════════════ */}
            <div className="v2-perm-phase-row">
              <div className="v2-sidebar-block phase-permission">権限設定</div>
              <div className="v2-perm-content-canvas">
                <div className="elements-layer-full">
                  <div
                    className="flow-card"
                    style={{
                      position: "absolute",
                      left: 615,
                      top: 14,
                      width: 172,
                      height: 68,
                    }}
                  >
                    <div className="card-label">コースごとに権限設定</div>

                    {isAdmin ? (
                      <button
                        className="card-action-btn"
                        onClick={() =>
                          window.open(
                            "permissions_management?tab=e_learning_permissions&isDisplayInNewTab=true",
                            "_blank",
                          )
                        }
                      >
                        権限管理 <ExternalLinkIcon />
                      </button>
                    ) : (
                      <StyledHoverTooltip bg="#2d3748" borderRadius={0}>
                        <div className="custom-tooltip-content">
                          権限がありません
                        </div>
                        <div style={{ width: "100%" }}>
                          <button className="card-action-btn disabled" disabled>
                            権限管理 <ExternalLinkIcon />
                          </button>
                        </div>
                      </StyledHoverTooltip>
                    )}
                    {isShowNote && (
                      <StyledCalloutBubble
                        placement="bottom"
                        style={{ top: 76, left: -44 }}
                      >
                        <p>
                          ①{" "}
                          <span style={{ fontWeight: 600 }}>
                            シートそのもの
                          </span>
                          の「閲覧・編集・削除」を設定
                          <br />
                          ② 役職・組織・人の３次元で制御可能
                          <br />③ 組織図の時間軸で制御されるので要注意
                        </p>
                      </StyledCalloutBubble>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                FULL SVG CONNECTOR LAYER
                - top: 32px (directly below top banner row)
                - height: 540px
                ══════════════════════════════════════════════════════════════ */}
            <svg
              style={{
                position: "absolute",
                top: 32,
                left: 84,
                width: 1296,
                height: 540,
                pointerEvents: "none",
                zIndex: 3,
              }}
              viewBox="0 0 1296 540"
              preserveAspectRatio="none"
            >
              <defs>
                <marker
                  id="flow-arrow"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#1e3a83" />
                </marker>
              </defs>

              {/* ──────────────────────────────────────────────────────────
                  1. ROUTE FROM Eラーニング (LAYER 1):
                  - From bottom of Eラーニング (81, 44) down to y = 56.
                  - Turns right horizontally from x = 81 to x = 1043.
                  - 3 downward arrows:
                    1. To コースを新規追加 (320, 76) -> Center 320px
                    2. To E-ラーニングを視聴・編集 (701, 76)
                    3. To 取引先とBizTreasureで動画共有 (1043, 76)
                  ────────────────────────────────────────────────────────── */}
              <path
                d="M 81 44 L 81 56 L 1060 56"
                fill="none"
                stroke="#1e3a83"
                strokeWidth="1.2"
              />

              {/* Arrow 1: Pointing down to top of コースを新規追加 (center 320px) */}
              <line
                x1="320"
                y1="56"
                x2="320"
                y2="76"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Arrow 2: Pointing down to top of E-ラーニングを視聴・編集 */}
              <line
                x1="701"
                y1="56"
                x2="701"
                y2="76"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Arrow 3: Pointing down to top of 取引先とBizTreasureで動画共有 */}
              <line
                x1="1060"
                y1="56"
                x2="1060"
                y2="76"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* ──────────────────────────────────────────────────────────
                  2. LEFT COLUMN (LAYER 2 -> 3 -> 4 -> 5):
                  - コースを新規追加 bottom: (320, 104).
                  - Diamond コースの追加方法 top: (320, 136), left: (235, 156), right: (405, 156).
                  - Card コース・レッスンの追加 top: (105, 200), right: (190, 234), bottom: (105, 268).
                  - Box 他社に動画を共有・販売 left: (220, 234), right: (380, 234).
                  - Card コース・レッスンをダウンロード top: (495, 200), left: (410, 234), bottom: (495, 268).
                  - Card レコード情報の閲覧・編集 (Shifted outside, aligned with E-ラーニングを視聴・編集): left: (615, 349), bottom: (701, 383).
                  - Box E-ラーニングを視聴・編集: bottom: (701, 104) -> Card レコード情報の閲覧・編集 top: (701, 315).
                  ────────────────────────────────────────────────────────── */}
              {/* Line: コースを新規追加 -> Diamond */}
              <line
                x1="320"
                y1="104"
                x2="320"
                y2="136"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Branch Left: Diamond -> Card コース・レッスンの追加 */}
              <path
                d="M 236 156 L 105 156 L 105 200"
                fill="none"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Branch Right: Diamond -> Card コース・レッスンをダウンロード (Extended from 405 -> 495px) */}
              <path
                d="M 400 156 L 537 156 L 537 200"
                fill="none"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Middle: Card コース・レッスンの追加 -> Box 他社に動画を共有・販売 */}
              <line
                x1="190"
                y1="234"
                x2="235"
                y2="234"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Middle: Box 他社に動画を共有・販売 -> Card コース・レッスンをダウンロード */}
              <line
                x1="407"
                y1="234"
                x2="437"
                y2="234"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Down: Box E-ラーニングを視聴・編集 -> Card レコード情報の閲覧・編集 */}
              <line
                x1="701"
                y1="104"
                x2="701"
                y2="315"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Route from コース・レッスンの追加 going down then turning right into レコード情報の閲覧・編集 */}
              <path
                d="M 105 268 L 105 349 L 615 349"
                fill="none"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Route from コース・レッスンをダウンロード going down and connecting into the horizontal line of コース・レッスンの追加 */}
              <line
                x1="537"
                y1="268"
                x2="537"
                y2="349"
                stroke="#1e3a83"
                strokeWidth="1.2"
              />

              {/* ──────────────────────────────────────────────────────────
                  3. RIGHT COLUMN:
                  - 取引先とBizTreasureで動画共有 bottom: (1043, 104).
                  - Diamond アカウントの有無 top: (1043, 136), left: (958, 156), right: (1088, 156).
                  - Card 契約のご提案 top: (911, 200).
                  - Card 情報共有の開通申請 top: (1166, 200), bottom: (1166, 268).
                  - Box 申請を承認 top: (1166, 315).
                  ────────────────────────────────────────────────────────── */}
              {/* Line: 取引先とBizTreasureで動画共有 -> Diamond */}
              <line
                x1="1060"
                y1="104"
                x2="1060"
                y2="136"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Branch Left: Diamond -> Card 情報共有の開通申請 */}
              <path
                d="M 976 156 L 924 156 L 924 200"
                fill="none"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Branch Right: Diamond -> Card 契約のご提案 */}
              <path
                d="M 1143 156 L 1199 156 L 1199 200"
                fill="none"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Down: Card 情報共有の開通申請 -> Box 申請を承認 */}
              <line
                x1="924"
                y1="268"
                x2="924"
                y2="315"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* Middle: Card 契約のご提案 -> Card 情報共有の開通申請 */}
              <line
                x1="1126"
                y1="234"
                x2="1011"
                y2="234"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />

              {/* ──────────────────────────────────────────────────────────
                  4. BOTTOM LAYER (権限設定):
                  - Card レコード情報の閲覧・編集 bottom: (701, 383).
                  - Card コースごとに権限設定 top: (701, 458) (430px + 14px + 14px).
                  ────────────────────────────────────────────────────────── */}
              <line
                x1="701"
                y1="383"
                x2="701"
                y2="458"
                stroke="#1e3a83"
                strokeWidth="1.2"
                markerEnd="url(#flow-arrow)"
              />
            </svg>
          </div>
        </div>
      </StyledFlowV2Wrapper>
    </ZoomContainer>
  );
};

const ElectronicLearningChartV2: FC<ReceivedProps> = (props) => (
  <ElectronicLearningChartV2Layout {...useElectronicLearningChart(props)} />
);

export default ElectronicLearningChartV2;
